<template>
  <FormularioCadastro
    module-name="Grupos de Acesso"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="submitFormComRegras"
    @cancelar="handleCancel"
    @show-notification="(payload) => emit('show-notification', payload)"
    :desabilitar-salvar="isReadOnly"
  >
    <!-- Frame 1: Dados Básicos -->
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.dadosBasicos = !accordionState.dadosBasicos"
      >
        <h5 class="mb-0"><i class="bi bi-info-circle me-2 text-primary"></i> Dados Básicos</h5>
        <i :class="['bi', accordionState.dadosBasicos ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.dadosBasicos">
        <div class="row g-3">
          <div class="col-md-6">
            <CampoInput
              id="nomeGrupo"
              label="Nome"
              v-model="item.nome"
              required
              :readonly="isReadOnly"
            />
          </div>
          <div class="col-12">
            <CampoInput
              id="descricaoGrupo"
              label="Descrição"
              type="textarea"
              v-model="item.descricao"
              :readonly="isReadOnly"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Frame 2: Regras de Acesso (Permissões) -->
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.regrasAcesso = !accordionState.regrasAcesso"
      >
        <h5 class="mb-0"><i class="bi bi-shield-check me-2 text-primary"></i> Regras de Acesso (Permissões)</h5>
        <i :class="['bi', accordionState.regrasAcesso ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.regrasAcesso">
        <div v-if="isLoadingPermissoes" class="text-center text-muted small py-4">
          <span class="spinner-border spinner-border-sm me-2"></span> A carregar permissões...
        </div>
        <div v-else-if="erroPermissoes" class="alert alert-warning border-0 shadow-sm">{{ erroPermissoes }}</div>
        <PermissoesAcessoSelector
          v-else
          :permissoesConsolidadas="permissoesConsolidadas"
          v-model:modelValue="permissoesSelecionadasIds"
          :disabled="isReadOnly"
          :exibirControleExpiracao="false"
          :exibirMapaPermissoes="true"
        />
      </div>
    </div>

    <!-- Frame 3: Configurações de Sessão / Token JWT -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0"><i class="bi bi-shield-lock me-2 text-primary"></i> Configurações de Sessão / Token JWT</h5>
      </div>
      <div class="card-body">
        <div class="alert alert-info border-0 py-2 px-3 small mb-3">
          <i class="bi bi-info-circle me-1"></i>
          Deixe em branco para herdar o valor configurado na empresa.
        </div>
        <div class="row g-3">
          <div class="col-md-4">
            <CampoInput
              id="tokenExpiracaoMinutos"
              label="Expiração do Access Token (minutos)"
              type="number"
              min="1"
              v-model.number="item.tokenExpiracaoMinutos"
              descricao-informativa="Deixe vazio para herdar da empresa."
            />
          </div>
        </div>
      </div>
    </div>

  </FormularioCadastro>
</template>

<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue'
import api from '@/services/api'
import { useFormulario } from '@/composables/useFormulario.js'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import PermissoesAcessoSelector from '@/components/base/permissoes/PermissoesAcessoSelector.vue'
import { useAuthStore } from '@/stores/auth.js'

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idGrupoAcesso: 0,
      nome: '',
      descricao: '',
      idEmpresa: null,
      tokenExpiracaoMinutos: null
    })
  }
})
const emit = defineEmits(['itemAtualizado', 'cancelar', 'changesDetected', 'show-notification'])

const authStore = useAuthStore()

const temPermissaoCompleta = computed(() => authStore.pode('GRUPOS_DE_ACESSO_COMPLETO'))
const isReadOnly = computed(() => {
  return isEditing.value && !temPermissaoCompleta.value
})

const {
  item, isLoading, isEditing, entityInfo, submitForm, handleCancel
} = useFormulario(props, emit)

const accordionState = reactive({
  dadosBasicos: true,
  regrasAcesso: false
})

// === PERMISSÕES DE ACESSO (via PermissoesAcessoSelector) ===
const permissoesConsolidadas = ref([])
const permissoesSelecionadasIds = ref([])
const isLoadingPermissoes = ref(false)
const erroPermissoes = ref(null)

const buscarPermissoesConsolidadas = async (idEmpresaOverride) => {
  isLoadingPermissoes.value = true
  erroPermissoes.value = null
  try {
    if (idEmpresaOverride) {
      const response = await api.get(`/EmpresasPermissoesAcesso/empresa/${idEmpresaOverride}/catalogo-contratado`)
      permissoesConsolidadas.value = Array.isArray(response.data) ? response.data : (response.data?.items || [])
    } else {
      // Sem empresa definida, carrega o catálogo completo
      const response = await api.get('/RegraAcessoPermissao/catalogo')
      permissoesConsolidadas.value = Array.isArray(response.data) ? response.data : (response.data?.items || [])
    }
  } catch (error) {
    console.error('Erro ao buscar permissões:', error)
    erroPermissoes.value = 'Não foi possível carregar as permissões de acesso.'
    permissoesConsolidadas.value = []
  } finally {
    isLoadingPermissoes.value = false
  }
}

const buscarPermissoesDoGrupo = async (idGrupo) => {
  if (!idGrupo) return
  try {
    const response = await api.get(`/GruposAcesso/${idGrupo}/permissoes`)
    permissoesSelecionadasIds.value = Array.isArray(response.data) ? response.data : []
  } catch (error) {
    console.error(`Erro ao buscar permissões para o grupo ${idGrupo}:`, error)
    emit('show-notification', { message: 'Erro ao carregar permissões associadas a este grupo.', type: 'error' })
    permissoesSelecionadasIds.value = []
  }
}

const obterIdEmpresaAtiva = () => {
  return authStore.empresaAtiva
}

const submitFormComRegras = async () => {
  if (isReadOnly.value) {
    console.warn('Tentativa de submit em modo read-only.')
    return
  }

  if (!authStore.empresaAtiva && authStore.isAdminInterno) {
    console.error('Admin interno tentando salvar grupo sem empresa alvo selecionada.')
    emit('show-notification', { message: 'Admin interno: Selecione uma empresa alvo.', type: 'error' })
    return
  }

  const permissoesIdsPuras = permissoesSelecionadasIds.value.slice()
  const payload = {
    Nome: item.value.nome,
    Descricao: item.value.descricao,
    IdEmpresa: Number(authStore.empresaAtiva),
    PermissoesIds: permissoesIdsPuras,
    TokenExpiracaoMinutos: item.value.tokenExpiracaoMinutos
  }
  if (isEditing.value && item.value.idGrupoAcesso) {
    payload.IdGrupoAcesso = item.value.idGrupoAcesso
  }

  await submitForm('GruposAcesso', 'idGrupoAcesso', payload)
}

onMounted(async () => {
  try {
    const idEmpresa = obterIdEmpresaAtiva()
    if (!idEmpresa && authStore.isAdminInterno) {
      erroPermissoes.value = 'Selecione uma empresa para carregar as permissões de acesso disponíveis.'
      isLoadingPermissoes.value = false
      return
    }
    await buscarPermissoesConsolidadas(idEmpresa)
    if (isEditing.value && item.value.idGrupoAcesso && !erroPermissoes.value) {
      await buscarPermissoesDoGrupo(item.value.idGrupoAcesso)
    }
  } catch (error) {
    console.error('Falha na inicialização do componente de cadastro de grupos:', error)
    if (!erroPermissoes.value) {
      erroPermissoes.value = 'Ocorreu um erro inesperado ao inicializar o formulário.'
    }
  }
})

watch(permissoesSelecionadasIds, () => {
  if (!isLoadingPermissoes.value) {
    emit('changes-detected', true)
  }
}, { deep: true })
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.accordion-body .list-group-item {
  border: none;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: transparent;
}
</style>
